import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import * as api from "../../api";
import IdleLockScreen from "./IdleLockScreen";
import { readTestMode, writeTestMode, clearAllClientData } from "./idleSessionStore";
import { beginLogout } from "../../utils/logoutGuard";

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("profile"))?.result || null;
  } catch (err) {
    return null;
  }
};

// SuperAdmin-only "test lock screen": counts down 5 seconds, then shows the
// exact lock screen stores see. Purely a preview - nothing is locked, no one
// is signed out, and it never touches the idle clock or any store's settings.
// The password check is the real one (the SuperAdmin's own password).
export default function IdleLockPreview({ seconds = 5 }) {
  const [left, setLeft] = useState(null);
  const [showing, setShowing] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  // Same as the real lock screen's Sign out: ends this session for real.
  const handleSignOut = async () => {
    beginLogout();
    try {
      await Promise.race([api.logoutSession(), new Promise((r) => setTimeout(r, 3000))]);
    } catch (err) {
      // Server unreachable - still sign out locally.
    }
    await clearAllClientData();
    dispatch({ type: "LOGOUT" });
    history.push("/");
  };

  useEffect(() => {
    if (left === null) return undefined;
    if (left <= 0) {
      setLeft(null);
      setShowing(true);
      return undefined;
    }
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  const handleUnlock = async (password) => {
    try {
      await api.unlockSession(password);
      setShowing(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err?.response?.data?.message || "Incorrect password" };
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={left !== null || showing}
        onClick={() => setLeft(seconds)}
        startIcon={<LockOutlinedIcon fontSize="small" />}
        style={{ textTransform: "none", marginTop: 10 }}
      >
        {left !== null ? `Lock screen appears in ${left}s…` : `Test lock screen (${seconds} sec)`}
      </Button>
      {showing && (
        <IdleLockScreen
          preview
          user={readUser()}
          businessName=""
          lockMinutes={1}
          secondsLeft={null}
          warnSeconds={0}
          attemptsLeft={5}
          onUnlock={handleUnlock}
          onSignOut={handleSignOut}
          onClosePreview={() => setShowing(false)}
        />
      )}
    </>
  );
}

// SuperAdmin switch (this browser only): makes the REAL screen lock fire after
// 5 seconds idle while viewing the POS (Go to POS in the top bar).
export function IdleTestModeToggle() {
  const [on, setOn] = useState(readTestMode);
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: "0.8rem", cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          writeTestMode(e.target.checked);
          setOn(e.target.checked);
        }}
      />
      <span>
        <b>Real 5-sec lock test in POS</b> - while on, use "Go to POS" and stay idle 5 seconds: the actual lock screen appears (only for you, this browser).
      </span>
    </label>
  );
}
